import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { createMarkup } from '../utils/createMarkup';
import { Panel } from './Panel';
import { Typography } from './Typography';
import { useDataLayerPush } from '../hooks/useDataLayerPush';

export interface CardTestimonialProps {
  backgroundColor?: 'white' | 'grey1';
  name: string;
  text?: string;
  onClick?: any;
  audience?: string;
}

export interface CardTestimonialWrapperProps {
  rest?: any;
}

const StyledAction = styled.div`
  ${({ theme: { space, borders, media } }) => css`
    padding-top: ${space.md};
    border-top: ${borders.default};
  `};
`;

const StyledCardTestimonial = styled.div`
  ${({ theme: { global, shadows } }) => css`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    text-align: left;
    border-radius: ${global.borderRadius.default};
    overflow: hidden;
    height: 100%;

    &:hover {
      box-shadow: ${shadows.small};
    }
  `};
`;

const StyledBody = styled(Panel)`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const StyledHeader = styled.div`
  flex-grow: 1;
`;

const Styledtext = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.md};
  `};
`;

const CardTestimonialWrapper: FC<React.PropsWithChildren<CardTestimonialWrapperProps>> = ({ children, rest }) => (
  <StyledCardTestimonial as="div" {...rest}>
    {children}
  </StyledCardTestimonial>
);

export const CardTestimonial: FC<React.PropsWithChildren<CardTestimonialProps>> = ({
  backgroundColor = 'grey1',
  name,
  text,
  onClick,
  audience,
  ...rest
}) => {
  const { dataLayerPush } = useDataLayerPush();

  const handleClick = (...props) => {
    dataLayerPush({
      event: 'testimonial card',
      clickText: text,
      clickBladeId: undefined,
    });
    if (onClick) {
      onClick(...props);
    }
  };

  return (
    <CardTestimonialWrapper onClick={handleClick} rest={rest}>
      <StyledBody backgroundColor={backgroundColor}>
        <StyledHeader>
          {text && (
            <Styledtext component="p" variant="bodyLarge">
              {createMarkup(text)}
            </Styledtext>
          )}
        </StyledHeader>
        {audience || name ? (
          <StyledAction>
            {name && (
              <>
                <Typography component="p" variant="titleSmall">
                  {name}
                </Typography>
              </>
            )}
            {audience}
          </StyledAction>
        ) : (
          ''
        )}
      </StyledBody>
    </CardTestimonialWrapper>
  );
};
